import { useState, useEffect } from "react";
import { createFilterOptions, getId, isValidId, updateActiveTask, Util } from '../../util/Util'
import RefreshButton from "../../components/RefreshButton";
import { useConversion } from "./ConversionContext";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { MIN_DATE, MYSQL_LIMITS } from "../../util/constants";
import { useAuth } from "../../hooks/useAuth";
import { Alert, Autocomplete, Grid, Link, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Link as RouterLink } from "react-router-dom";
import ConversionTypePicker from "./ConversionTypePicker";
import { ConversionKind, effectiveFactor, inputQtyFromStored, quantitiesFromInput } from "../../util/conversionMath";
import { businessTodayDate, parseISODay } from "../../util/businessClock";
import { canRecord, canSee } from '../../util/access';

interface Props {
	setValidateCheck: (value: boolean) => void;
}

const ConversionDetail = ({ setValidateCheck }: Props) => {
	const [depots, setDepots] = useState<any[]>([]);
	const [isClientLoading, setIsClientLoading] = useState<boolean>(false);
	const [isDepotLoading, setIsDepotLoading] = useState<boolean>(false);
	const [errorInput, setErrorInput] = useState("");
	const [clients, setClients] = useState<any[]>([]);

	const { setPendingTasks, activeTask } = useConversion();
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { api, access } = useAuth();

	const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;
	const canOpenProduction = canSee(access, 'production')
		|| canRecord(access, 'production');

	const fetchClients = async () => {
		setIsClientLoading(true);

		const res = await api.get("/clients",
			{
				params: {
					Active: true,
					Type: "WORKER"
				}
			}
		);

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
				setClients(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchClients);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsClientLoading(false);
	};

	const fetchDepots = async () => {
		setIsDepotLoading(true);

		const res = await api.get("/depot/meterred", { params: { Operation: "Conversion" } });

		if (res) {
			if (res.status === 200) {
				setDepots(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchDepots);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsDepotLoading(false);
	};

	const onReceiverChanged = (receiver: number) => {
		updateActiveTask(setPendingTasks, { receiver });
	};

	const onRefChanged = (reference: string = Util.getConversionNumber()) => {
		updateActiveTask(setPendingTasks, { reference });
	};

	const onDateChanged = (taskDate: Date = businessTodayDate()) => {
		updateActiveTask(setPendingTasks, { date: Util.formatISODate(taskDate) });
	};

	const onDepotChanged = (depotId: number) => {
		updateActiveTask(setPendingTasks, { depot: depotId });
	};

	const onTypeChanged = (convType: ConversionKind) => {
		const prevFactor = effectiveFactor(activeTask?.convType, activeTask?.factor || 1);
		const currentInput = inputQtyFromStored(
			activeTask?.convType,
			Number(activeTask?.quantity) || 0,
			Number(activeTask?.produce_qty) || 0,
			prevFactor,
		);
		const factor = effectiveFactor(convType, activeTask?.factor || 1);
		const next = quantitiesFromInput(convType, currentInput, factor);
		updateActiveTask(setPendingTasks, {
			convType,
			factor,
			produce_qty: Number.isFinite(next.produceQty) && next.produceQty > 0 ? next.produceQty : 1,
			quantity: Number.isFinite(next.sourceQty) && next.sourceQty > 0 ? next.sourceQty : 0,
		});
	};

	useEffect(() => {
		fetchClients();
		fetchDepots();
	}, []);

	useEffect(() => {
		if (clients.length > 0) {
			const receiverId = getId(clients, activeTask?.receiver);
			if (receiverId !== activeTask?.receiver) {
				onReceiverChanged(receiverId);
			}
		}
	}, [activeTask, clients]);

	useEffect(() => {
		const isValid = isValidId(clients, activeTask?.receiver) &&
			isValidId(depots, activeTask?.depot) && !!activeTask?.convType;

		setValidateCheck(isValid);
	}, [activeTask, clients, depots]);

	return (
		<Grid container spacing={0}>
			<Grid size={12}>
				<ConversionTypePicker
					value={activeTask?.convType || ""}
					onChange={onTypeChanged}
					disabled={!activeTask}
				/>
			</Grid>
			<Grid size={12} sx={{ px: 0.5, pb: 1 }}>
				<Alert severity="info" variant="outlined">
					{t("common:conversion.productionHint")}
					{canOpenProduction && (
						<>
							{" "}
							<Link component={RouterLink} to="/stock/production">
								{t("common:conversion.productionLink")}
							</Link>
						</>
					)}
				</Alert>
			</Grid>
			<Grid size={{ xs: 6, sm: 3, md: 3 }} >
				<Autocomplete
					id="clientInput"
					size="small"
					options={clients}
					filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
					getOptionLabel={(client) => client.acc_name || ""}
					value={clients.find((c) => c.id === activeTask?.receiver) || null}
					onChange={(_, newValue) => {
						onReceiverChanged(newValue?.id || -1);
					}}
					loading={isClientLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isClientLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:conversion.observer")}:&nbsp;&nbsp;
									<RefreshButton isWorking={isClientLoading} onClick={fetchClients} disabled={isClientLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 3, md: 3 }} >
				<TextField
					id="referenceInput"
					size="small"
					label={
						<span>
							{t("common:tables.reference")}:&nbsp;&nbsp;
							<RefreshButton isWorking={false} onClick={onRefChanged} />
						</span>
					}
					variant="filled"
					fullWidth
					type="text"
					value={activeTask?.reference || ""}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
					onBlur={(e) => {
						if (!e.target.value || e.target.value.trim() === "") {
							onRefChanged();
						}
					}}
					onChange={(e) => onRefChanged(e.target.value.trim())}
					disabled={!activeTask}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 3, md: 3 }} >
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={
							<span>
								{t("common:tables.date")}:&nbsp;&nbsp;
								<RefreshButton isWorking={false} onClick={onDateChanged} />
							</span>
						}
						value={activeTask?.date ? parseISODay(activeTask?.date) as Date : businessTodayDate()}
						minDate={(parseISODay(minDate) as Date)}
						onChange={(newDate) => {
							if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < (parseISODay(minDate) as Date)) {
								setErrorInput("dateInput");
								setTimeout(() => setErrorInput(""), 1500);
								return;
							}
							const date = newDate instanceof Date ? newDate : newDate.toDate();
							onDateChanged(date);
						}}
						disabled={!activeTask}
						slotProps={{
							textField: {
								id: "dateInput",
								size: "small",
								fullWidth: true,
								variant: "filled",
								error: errorInput === "dateInput",
								helperText:
									errorInput === "dateInput" ? t("common:validation.invalidDate") : "",
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid size={{ xs: 6, sm: 3, md: 3 }} >
				<Autocomplete
					id="depotInput"
					size="small"
					options={depots}
					filterOptions={createFilterOptions((opt) => opt.DepotName || "")}
					getOptionLabel={(depot) => depot.DepotName || ""}
					value={depots.find((d) => d.id === activeTask?.depot) || null}
					onChange={(_, newValue) => {
						onDepotChanged(newValue?.id || -1);
					}}
					loading={isDepotLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isDepotLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:tables.depot")}:&nbsp;&nbsp;
									<RefreshButton isWorking={isDepotLoading} onClick={fetchDepots} disabled={isDepotLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
		</Grid>
	);
};

export default ConversionDetail;
